import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MinisiteLayout from '../../../layout/Minisite';
import MinisitePageTableItem from './MinisitePageTableItem';
import ButtonPublish from '../../../components/_button/ButtonPublish';
import useNotification from '../../../components/_hook/useNotification';
import ButtonUnpublish from '../../../components/_button/ButtonUnpublish';
import GraphQLTable from '../../../components/_table/GraphQLTable';
import Helmet from '../../../components/Helmet';
import { IMinisiteSchema } from '../../../../graphql/type/minisite';
import { usePublishMinisitePagesMutation } from '../../../../graphql/mutation/minisite/minisitePage/publishMinisitePagesMutation';
import { useUnpublishMinisitePagesMutation } from '../../../../graphql/mutation/minisite/minisitePage/unpublishMinisitePagesMutation';
import useRouter from '../../../components/_hook/useRouter';
import {
  IMinisiteQueryData,
  IMinisiteVars,
  useMinisiteQuery,
} from '../../../../graphql/query/minisite/useMinisiteQuery';
import Context from '../../../context';

function MinisitePageTable() {
  const { t } = useTranslation();
  const { notification } = useNotification();
  const { params } = useRouter();
  const { company } = useContext(Context);
  const { docID: minisiteId } = params;
  const [selectedItems, setSelectedItems] = useState<
    IMinisiteSchema['configuration']['pages'][0][]
  >([]);

  const [publishMinisitePagesMutation] = usePublishMinisitePagesMutation(
    `updatedMinisite {
				_id
				configuration {
                  pages {
                    page
                    status
                  }
                }
			}`,
    {
      onCompleted: () => {
        notification.success(
          t('__item___updated', {
            item: `${selectedItems.length} ${t('pages', {
              count: selectedItems.length,
            })}`,
          })
        );
        setSelectedItems([]);
      },
      onError: (e) => {
        notification.error(e.message);
      },
    }
  );
  const [unpublishMinisitePagesMutation] = useUnpublishMinisitePagesMutation(
    `updatedMinisite {
				_id
				configuration {
                  pages {
                    page
                    status
                  }
                }
			}`,
    {
      onCompleted: () => {
        notification.success(
          t('__item___updated', {
            item: `${selectedItems.length} ${t('pages', {
              count: selectedItems.length,
            })}`,
          })
        );
        setSelectedItems([]);
      },
      onError: (e) => {
        notification.error(e.message);
      },
    }
  );

  async function onClickPublish() {
    await publishMinisitePagesMutation({
      variables: {
        minisiteId,
        pages: selectedItems.map((item) => item.page),
      },
    });
  }

  async function onClickUnpublish() {
    await unpublishMinisitePagesMutation({
      variables: {
        minisiteId,
        pages: selectedItems.map((item) => item.page),
      },
    });
  }

  return (
    <>
      <Helmet title={`${t('page')} | ${t('minisite')}`} />
      <GraphQLTable<
        IMinisiteQueryData,
        IMinisiteSchema['configuration']['pages'][0],
        IMinisiteVars
      >
        select={`
				_id
				configuration {
                  pages {
                    page
                    status
                  }
                }
       	 `}
        useQuery={useMinisiteQuery}
        renderHeader={[
          {
            field: 'page',
            label: t('page'),
            align: 'left',
          },
          {
            field: 'action',
            label: t('action'),
            align: 'right',
          },
        ]}
        renderItem={(item) => <MinisitePageTableItem item={item} />}
        useCheckbox
        onItemSelected={setSelectedItems}
        selectedItems={selectedItems}
        defaultFilterOptions={{
          companyId: company._id,
          _id: minisiteId,
        }}
        title={t('page')}
        defaultSort="_id"
        actionButtons={[
          <ButtonPublish
            key="publish"
            variant="contained"
            color="primary"
            size="small"
            text={t('publish')}
            onClick={onClickPublish}
          />,
          <ButtonUnpublish
            key="unpublish"
            color="primary"
            variant="contained"
            size="small"
            text={t('unpublish')}
            onClick={onClickUnpublish}
          />,
        ]}
        itemKey="page"
        useFilterDataItemCount
        filterData={(data) => {
          const minisite = data.length
            ? ((data[0] as unknown) as IMinisiteSchema)
            : null;
          const pages =
            minisite && minisite.configuration && minisite.configuration.pages
              ? minisite.configuration.pages
              : [];
          const pageHome = pages.find((page) => page.page === 'home');
          const pageAbout = pages.find((page) => page.page === 'about');
          const pageTeam = pages.find((page) => page.page === 'team');
          const pageShowcase = pages.find((page) => page.page === 'showcase');
          const pageAnnounce = pages.find((page) => page.page === 'announce');
          const pageContact = pages.find((page) => page.page === 'contact');
          return [
            {
              page: 'home',
              status: pageHome ? pageHome.status : false,
            },
            {
              page: 'about',
              status: pageAbout ? pageAbout.status : false,
            },
            {
              page: 'team',
              status: pageTeam ? pageTeam.status : false,
            },
            {
              page: 'showcase',
              status: pageShowcase ? pageShowcase.status : false,
            },
            {
              page: 'announce',
              status: pageAnnounce ? pageAnnounce.status : false,
            },
            {
              page: 'contact',
              status: pageContact ? pageContact.status : false,
            },
          ];
        }}
      />
    </>
  );
}

export default () => <MinisiteLayout main={<MinisitePageTable />} />;
